// =============================================================================
// kidGender (frontend) — Issue #1853.
//
// The browser half of supabase/functions/_shared/kidGender.ts. It carries ONLY
// the stored vocabulary and the button labels; the prompt phrasing lives in the
// shared file because its only two callers are edge functions.
//
// ⚠️ The value strings here are the SAME strings the shared module parses, and
// nothing at runtime connects the two files — src/ never imports from
// supabase/functions/ (only tests do). A typo on either side is silent: the
// write succeeds, the fact is stored, and the briefing goes on saying "she".
// src/lib/__tests__/kidGender.test.ts imports both and asserts they match.
// Same mirror-plus-parity-test shape as suppressPlanDuplicates.ts.
// =============================================================================

/** The only two values ever stored. Absence is the third state — see below. */
export type KidGender = "boy" | "girl";

export const KID_GENDER_VALUES: readonly KidGender[] = ["boy", "girl"] as const;

/** `family_context.key`. Must equal KID_GENDER_FACT_KEY in the shared module. */
export const KID_GENDER_FACT_KEY = "gender";

/**
 * Button copy. Capitalised for the UI; the STORED value stays lowercase, which
 * is why these are a map and not a `toUpperCase()` of the value — a display
 * transform applied to a stored value is how the two quietly become one thing.
 */
export const KID_GENDER_LABELS: Record<KidGender, string> = {
  boy: "Boy",
  girl: "Girl",
};

/**
 * Coerce a stored value to a known gender, or null.
 *
 * ⚠️ null means "she never set one", and the UI must render that as NEITHER
 * button selected — not as a default. Defaulting to one of the two would put
 * the guess this whole issue removes back into the product, with a confident
 * checkmark on it.
 *
 * #1862 — accepts what a MODEL says, not only what the toggle writes.
 *
 * The stored vocabulary stays exactly "boy" | "girl" (the two UI pills). But a
 * [LEARN] tag or a voice remember_fact can carry the word she used — "son",
 * "daughter" — or a model's paraphrase of it — "male", "female". Dropping those
 * as unset would store the fact, light nothing, and leave the briefing saying
 * "they" about a kid she just called her son: remembered on disk, forgotten in
 * every sentence. Liberal in what is accepted, strict in what the UI writes.
 *
 * ⚠️ Deliberately NOT accepting "m"/"f"/"b"/"g" — too short to be a statement
 * rather than a stray character, and "they"/"unknown"/"nonbinary" stay null,
 * which the prompt rule turns into "they". Nothing here infers from a NAME.
 */
export function normalizeKidGender(raw: unknown): KidGender | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim().toLowerCase();
  if (v === "boy" || v === "son" || v === "male") return "boy";
  if (v === "girl" || v === "daughter" || v === "female") return "girl";
  return null;
}
